/**
 * WizardSummaryStep — WSPÓLNY krok „Podsumowanie" dla wszystkich kreatorów
 * (hodowla / suka / reproduktor / miot / firma). Jedno źródło prawdy, design wg makiety:
 *
 *  - nagłówek + 4 karty korzyści
 *  - interaktywna checklista „Czy wszystko jest gotowe?" — każdy wiersz ma status (Gotowe / do uzupełnienia),
 *    PODPOWIEDŹ co i dlaczego warto poprawić (prowadzenie klienta „za rękę") oraz przycisk
 *    Edytuj/Dodaj/Uzupełnij skaczący do właściwego kroku
 *  - sekcja „Zanim opublikujesz" (3 zgody) + przyciski Opublikuj / Zapisz i wróć później
 *
 * Konfigurację (checklist, etykiety) dostarcza każdy kreator — komponent tylko renderuje.
 */
import { useState } from 'react';
import {
  Sparkles, Globe, Search, MessageSquare, Share2,
  Building2, PawPrint, MapPin, Image as ImageIcon, Images, FileText, Phone, Mail,
  Heart, Dna, Crown, BadgeDollarSign, Baby, Users, Briefcase, Star, ListChecks, GitBranch, Dog,
  CheckCircle2, Pencil, Plus, Loader2, ArrowRight, Lightbulb,
} from 'lucide-react';

const ICONS = {
  building: Building2, paw: PawPrint, map: MapPin, photo: ImageIcon, gallery: Images,
  text: FileText, phone: Phone, mail: Mail, heart: Heart, dna: Dna, crown: Crown,
  money: BadgeDollarSign, puppies: Baby, parents: Users, briefcase: Briefcase,
  star: Star, services: ListChecks, pedigree: GitBranch, dog: Dog,
};

function Consent({ checked, onChange, children, testId }) {
  return (
    <div
      role="checkbox"
      aria-checked={!!checked}
      tabIndex={0}
      onClick={() => onChange(!checked)}
      onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onChange(!checked); } }}
      className="flex items-start gap-3 cursor-pointer group select-none"
    >
      <span
        data-testid={testId}
        aria-checked={!!checked}
        className={`mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 transition-all ${
          checked
            ? 'bg-[var(--apisai-primary)] border-[var(--apisai-primary)]'
            : 'bg-white border-zinc-300 group-hover:border-[var(--apisai-primary)]'
        }`}
      >
        {checked && <CheckCircle2 className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
      </span>
      <span className="text-[13.5px] leading-relaxed text-[var(--apisai-text-secondary)]">{children}</span>
    </div>
  );
}

export default function WizardSummaryStep({
  headerTitle = 'Twój profil jest gotowy do publikacji',
  headerSubtitle = 'Po publikacji pojawisz się w katalogu APISAI i otrzymasz własną stronę z formularzem kontaktowym.',
  pageCardTitle = 'Twoja strona',
  checklist = [],
  goToStep,
  catalogChecked,
  onCatalogChange,
  catalogLabel = 'Chcę być widoczny w katalogu APISAI.',
  catalogTestId = 'wizard-catalog-toggle',
  termsAccepted,
  setTermsAccepted,
  termsTestId = 'wizard-terms-checkbox',
  dataAccurateTestId = 'wizard-data-accurate',
  onPublish,
  onSaveLater,
  submitting = false,
  isEdit = false,
  publishLabel = 'Opublikuj',
  saveLabel = 'Zapisz i wróć później',
  // back-compat: stare wywołania mogły przekazać summaryRows zamiast checklist
  summaryRows,
  testId = 'wizard-summary',
}) {
  const [dataAccurate, setDataAccurate] = useState(false);

  // back-compat: zmapuj proste summaryRows (label/value) na checklistę
  const rows = (checklist && checklist.length)
    ? checklist
    : (summaryRows || []).map((r) => ({
        icon: 'text', label: r.label, value: r.value || 'Do uzupełnienia',
        ok: !!r.value, step: r.step, addLabel: 'Uzupełnij',
      }));

  const features = [
    { icon: Globe, title: pageCardTitle, desc: 'Własna strona z linkiem do udostępniania.' },
    { icon: Search, title: 'Wpis w katalogu', desc: 'Widoczność w katalogu APISAI.' },
    { icon: MessageSquare, title: 'Formularz zapytań', desc: 'Zapytania od klientów prosto do Ciebie.' },
    { icon: Share2, title: 'Materiały do promocji', desc: 'Gotowe linki i grafiki do social media.' },
  ];

  const requiredOk = rows.filter((r) => r.required).every((r) => r.ok);
  const consentsOk = isEdit || (termsAccepted && dataAccurate);
  const canPublish = requiredOk && consentsOk && !submitting;
  const doneCount = rows.filter((r) => r.ok).length;

  return (
    <div className="space-y-8" data-testid={testId}>
      {/* Nagłówek */}
      <header>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--apisai-primary)]/10 text-[var(--apisai-primary)] text-[12px] font-semibold mb-3">
          <Sparkles className="w-3.5 h-3.5" /> Ostatni krok · Podsumowanie
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[var(--apisai-text-primary)] leading-tight">{headerTitle}</h1>
        <p className="text-[14px] text-[var(--apisai-text-secondary)] mt-2 max-w-xl">{headerSubtitle}</p>
      </header>

      {/* 4 karty korzyści */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {features.map((f) => (
          <div key={f.title} className="rounded-xl border border-zinc-200 bg-white p-4">
            <div className="w-9 h-9 rounded-lg bg-[var(--apisai-primary)]/10 flex items-center justify-center mb-3">
              <f.icon className="w-[18px] h-[18px] text-[var(--apisai-primary)]" />
            </div>
            <div className="text-[13.5px] font-semibold text-[var(--apisai-text-primary)]">{f.title}</div>
            <div className="text-[12px] text-[var(--apisai-text-secondary)] mt-1 leading-snug">{f.desc}</div>
          </div>
        ))}
      </div>

      {/* Checklista */}
      <section>
        <div className="flex items-center justify-between gap-3 mb-1">
          <h2 className="text-[16px] font-bold text-[var(--apisai-text-primary)]">Czy wszystko jest gotowe?</h2>
          <span className="text-[12px] font-semibold text-[var(--apisai-text-secondary)] whitespace-nowrap">{doneCount}/{rows.length} gotowe</span>
        </div>
        <p className="text-[13px] text-[var(--apisai-text-secondary)] mb-3">Im więcej informacji, tym większe zaufanie klientów i więcej zapytań.</p>
        <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden" data-testid="summary-checklist">
          {rows.map((row, i) => {
            const Icon = ICONS[row.icon] || FileText;
            const slug = (row.label || `row-${i}`).split(' ')[0].toLowerCase();
            return (
              <div
                key={row.label || i}
                data-testid={`summary-row-${slug}`}
                className={`flex items-start gap-3 px-4 py-3.5 ${i ? 'border-t border-zinc-100' : ''}`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${row.ok ? 'bg-emerald-50' : 'bg-[var(--apisai-primary)]/10'}`}>
                  <Icon className={`w-[18px] h-[18px] ${row.ok ? 'text-emerald-600' : 'text-[var(--apisai-primary)]'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-medium text-[var(--apisai-text-primary)] flex items-center gap-2">
                    {row.label}
                    {row.required && !row.ok && <span className="text-[10.5px] font-semibold text-[var(--apisai-primary)] bg-[var(--apisai-primary)]/10 px-1.5 py-0.5 rounded">wymagane</span>}
                  </div>
                  <div className={`text-[12.5px] truncate ${row.ok ? 'text-zinc-500' : 'text-[var(--apisai-primary)] font-medium'}`}>{row.value}</div>
                  {/* Podpowiedź „za rękę" — pokazujemy gdy pole niegotowe */}
                  {!row.ok && row.hint && (
                    <div className="flex items-start gap-1.5 mt-1 text-[11.5px] text-zinc-400 leading-snug">
                      <Lightbulb className="w-3 h-3 mt-0.5 flex-shrink-0 text-amber-400" />
                      <span>{row.hint}</span>
                    </div>
                  )}
                </div>
                {row.ok && (
                  <span className="hidden sm:inline-flex items-center gap-1 text-[11.5px] font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full flex-shrink-0 mt-0.5">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Gotowe
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => goToStep && row.step && goToStep(row.step)}
                  data-testid={`summary-action-${slug}`}
                  className={`inline-flex items-center gap-1.5 text-[12.5px] font-semibold rounded-lg px-3 h-9 flex-shrink-0 transition-colors mt-0.5 ${
                    row.ok ? 'text-zinc-600 hover:bg-zinc-100' : 'text-white bg-[var(--apisai-primary)] hover:brightness-95'
                  }`}
                >
                  {row.ok ? <><Pencil className="w-3.5 h-3.5" /> Edytuj</> : <><Plus className="w-3.5 h-3.5" /> {row.addLabel || 'Uzupełnij'}</>}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Zanim opublikujesz */}
      <section className="rounded-xl border border-zinc-200 bg-zinc-50/60 p-5 space-y-3.5">
        <h2 className="text-[15px] font-bold text-[var(--apisai-text-primary)]">Zanim opublikujesz</h2>
        {onCatalogChange && (
          <Consent checked={!!catalogChecked} onChange={onCatalogChange} testId={catalogTestId}>
            {catalogLabel}
          </Consent>
        )}
        <Consent checked={dataAccurate} onChange={setDataAccurate} testId={dataAccurateTestId}>
          Potwierdzam, że podane informacje są prawdziwe i aktualne.
        </Consent>
        <Consent checked={!!termsAccepted} onChange={setTermsAccepted} testId={termsTestId}>
          Akceptuję{' '}
          <a href="/regulamin" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[var(--apisai-primary)] underline">regulamin</a>
          {' '}oraz{' '}
          <a href="/polityka-prywatnosci" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[var(--apisai-primary)] underline">politykę prywatności</a>
          {' '}APISAI.
        </Consent>
      </section>

      {/* Akcje */}
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onPublish}
          disabled={!canPublish}
          data-testid="wizard-publish-btn"
          className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-[var(--apisai-text-primary,#18181b)] text-white text-[14px] font-semibold whitespace-nowrap hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex-1 min-w-[220px]"
        >
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Publikuję…</> : <><Globe className="w-4 h-4 flex-shrink-0" /> {publishLabel}</>}
        </button>
        <button
          type="button"
          onClick={onSaveLater}
          disabled={submitting}
          data-testid="wizard-save-later-btn"
          className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl border border-zinc-300 text-[var(--apisai-text-primary)] text-[14px] font-semibold whitespace-nowrap hover:bg-zinc-50 transition-colors disabled:opacity-50 flex-1 min-w-[180px]"
        >
          {saveLabel}
        </button>
      </div>

      {!requiredOk && (
        <p className="text-[12.5px] text-[var(--apisai-primary)] flex items-center gap-1.5">
          <ArrowRight className="w-3.5 h-3.5" /> Uzupełnij pola oznaczone jako „wymagane", aby opublikować.
        </p>
      )}
      <p className="text-[12px] text-zinc-400">
        Po publikacji otrzymasz link do swojej strony, który możesz od razu udostępnić klientom.
      </p>
    </div>
  );
}
